import React, { useEffect, useState } from "react";
import {
  TEAM_MOVE_NO_SALES,
  TEAM_MOVE_NO_TARGET,
  addableSales,
  moveTargets,
  personLabel,
  teamSales,
} from "../../lib/teamManagement";

/**
 * Добави търговец / Премести търговец — one dialog, two questions.
 *
 * „Добави“ offers only Търговци who are in NO active team; somebody who already
 * belongs elsewhere is moved out of their OWN team, because the server refuses a
 * silent second membership and a move is a deliberate, atomic operation.
 *
 * A move is SOURCE-CENTRIC: the dialog opens on the team the person is in today,
 * asks which of ITS Търговци moves and into which OTHER active team. The clicked
 * team is therefore the source, never the destination.
 *
 * A move changes MEMBERSHIP ONLY. Offers stay with the person who owns them —
 * that is deliberate in this phase.
 */
export const TeamMemberDialog = ({
  open,
  mode,
  team,
  teams,
  eligibleSales,
  submitting,
  error,
  onCancel,
  onSubmit,
}) => {
  const [memberId, setMemberId] = useState("");
  const [destinationId, setDestinationId] = useState("");

  useEffect(() => {
    if (open) {
      setMemberId("");
      setDestinationId("");
    }
  }, [open, mode, team?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  if (!open || !team) return null;

  const moving = mode === "move";
  const options = moving
    ? teamSales(team)
    : addableSales(eligibleSales, team.id).filter(
        (s) => !(team.members || []).some((m) => m.member_id === s.member_id),
      );
  const destinations = moving ? moveTargets(teams, team.id) : [];

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-member-title"
        data-testid="team-member-dialog"
      >
        <div className="h" id="team-member-title">
          {moving ? "Преместване на търговец" : "Добавяне на търговец"}
        </div>
        <div className="body">
          <p style={{ margin: 0, fontSize: 13, color: "#475569" }} data-testid="team-member-target">
            {moving ? "Текущ екип: " : "Екип: "}
            {team.name}
          </p>

          <div className="qw-field" style={{ marginTop: 12 }}>
            <label htmlFor="team-member-select">Търговец</label>
            <select
              id="team-member-select"
              className="qw-select"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              disabled={submitting}
              data-testid="team-member-select"
            >
              <option value="">Изберете търговец</option>
              {options.map((s) => (
                <option
                  key={s.member_id}
                  value={s.member_id}
                  data-testid={`team-member-option-${s.member_id}`}
                >
                  {personLabel(s)}
                </option>
              ))}
            </select>
            {options.length === 0 && (
              <div className="qw-help" data-testid="team-member-empty">
                {moving
                  ? TEAM_MOVE_NO_SALES
                  : "Няма свободни търговци. Търговец в друг екип се премества от неговия екип."}
              </div>
            )}
          </div>

          {moving && (
            <div className="qw-field" style={{ marginTop: 12 }}>
              <label htmlFor="team-member-destination">Нов екип</label>
              <select
                id="team-member-destination"
                className="qw-select"
                value={destinationId}
                onChange={(e) => setDestinationId(e.target.value)}
                disabled={submitting}
                data-testid="team-member-destination"
              >
                <option value="">Изберете нов екип</option>
                {destinations.map((t) => (
                  <option
                    key={t.id}
                    value={t.id}
                    data-testid={`team-member-destination-option-${t.id}`}
                  >
                    {t.name}
                  </option>
                ))}
              </select>
              {destinations.length === 0 && (
                <div className="qw-help" data-testid="team-member-destination-empty">
                  {TEAM_MOVE_NO_TARGET}
                </div>
              )}
              <div className="qw-help">Само членството в екипа се променя. Офертите не се преразпределят.</div>
            </div>
          )}

          {error && (
            <div className="qw-error" role="alert" style={{ marginTop: 10 }} data-testid="team-member-error">
              {error}
            </div>
          )}
        </div>
        <div className="footer">
          <button
            type="button"
            className="qw-btn ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="team-member-cancel"
          >
            Отказ
          </button>
          <button
            type="button"
            className="qw-btn primary"
            onClick={() =>
              onSubmit(
                moving
                  ? {
                      member_id: memberId,
                      from_team_id: team.id,
                      to_team_id: destinationId,
                    }
                  : { member_id: memberId },
              )
            }
            disabled={submitting || !memberId || (moving && !destinationId)}
            data-testid="team-member-submit"
          >
            {submitting ? "Записване…" : moving ? "Премести" : "Добави"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberDialog;
